import * as _ from 'lodash';
import * as moment from 'moment';

import { AttributeDefinitionValueListItem } from 'src/app/shared/models/attributes/attributeDefinitionValueListItem';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { AttributeEntityType } from 'src/app/shared/models/attributes/attributeEntityType';
import { RankedKeyValues } from 'src/app/shared/models/attributes/rankedKeyValues';
import { ComplexAttributeSubFieldValue } from './complexAttributeSubFieldValue';
import { AttributeDefinitionSubFieldItem } from './attributeDefinitionSubFieldItem';

export class Attribute {
  public id: string;
  public name: string;
  public description: string;
  public adminDocumentation: string;
  public value: string;
  public type: string;
  public entityType: string;
  public attributeDefinitionId: string;
  public required: boolean;
  public isReadOnly: boolean;
  public isHidden: boolean;
  public featured: boolean;
  public allowsUserAddedValues: boolean;
  public attributeDefinitionValueListItem: AttributeDefinitionValueListItem;
  public complexAttributeSubFieldValue: ComplexAttributeSubFieldValue;
  public valueList: AttributeDefinitionValueListItem[];
  public subFieldList: AttributeDefinitionSubFieldItem[];
  public createdBy: string;
  public createdOn: Date;
  public updatedBy: string;
  public updatedOn: Date;
  public defaultValue:
    | string
    | boolean
    | AttributeDefinitionValueListItem
    | AttributeDefinitionValueListItem[];
  public edited: boolean;
  constructor(
    id: string,
    name: string,
    description: string,
    adminDocumentation: string,
    value: string,
    type: string,
    entityType: string,
    attributeDefinitionId: string,
    required: boolean,
    isReadOnly: boolean,
    isHidden: boolean,
    featured: boolean,
    allowsUserAddedValues: boolean,
    createdBy: string,
    createdOn: Date,
    updatedBy: string,
    updatedOn: Date,
    edited: boolean = false
  ) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.adminDocumentation = adminDocumentation;
    this.value = value;
    this.type = type;
    this.entityType = entityType;
    this.attributeDefinitionId = attributeDefinitionId;
    this.required = required;
    this.isReadOnly = isReadOnly;
    this.isHidden = isHidden;
    this.featured = featured;
    this.allowsUserAddedValues = allowsUserAddedValues;
    this.createdBy = createdBy;
    this.createdOn = createdOn;
    this.updatedBy = updatedBy;
    this.updatedOn = updatedOn;
    this.edited = edited;
  }

  containsSearchValue(searchValue: string) {
    return this.value.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
  }

  hasValue(): boolean {
    let hasValue = false;
    if (
      this.isDropDownType() ||
      this.isCheckboxType() ||
      this.isMultiSelectType() ||
      this.isSkillType() ||
      this.isStarRatingType()
    ) {
      hasValue = !_.isNil(this.attributeDefinitionValueListItem);
    } else if (this.isComplexType()) {
      hasValue = this.complexAttributeSubFieldValue?.hasValue();
    } else if (typeof this.value === 'string') {
      hasValue = !_.isEmpty(this.value.trim());
    } else if (typeof this.value === 'number') {
      hasValue = !isNaN(Number(this.value));
    } else if (this.isDateType()) {
      hasValue = this.value !== undefined && moment(this.value, 'MM/DD/YYYY').isValid();
    }
    return hasValue;
  }

  getValue() {
    if (this.isDropDownType() || this.isSkillType() || this.isStarRatingType()) {
      return this.attributeDefinitionValueListItem;
    } else if (this.isMultiSelectType()) {
      return this.valueList.length ? this.valueList : false;
    } else if (this.isCheckboxType()) {
      return this.valueList;
    } else {
      return this.value;
    }
  }

  isDateType(): boolean {
    return this.type === AttributeUiType.Date;
  }

  isDropDownType(): boolean {
    return this.type === AttributeUiType.DropDown;
  }

  isNumberType(): boolean {
    return this.type === AttributeUiType.Number;
  }

  isShortAnswerType(): boolean {
    return this.type === AttributeUiType.ShortAnswer;
  }

  isEncryptedFieldType(): boolean {
    return this.type === AttributeUiType.EncryptedField;
  }

  isLongAnswerType(): boolean {
    return this.type === AttributeUiType.LongAnswer;
  }

  isCheckboxType(): boolean {
    return this.type === AttributeUiType.Checkboxes;
  }

  isMultiSelectType(): boolean {
    return this.type === AttributeUiType.MultiSelect;
  }

  isSkillType(): boolean {
    return this.type === AttributeUiType.Skill;
  }

  isStarRatingType(): boolean {
    return this.type === AttributeUiType.StarRating;
  }

  isDocumentType(): boolean {
    return this.type === AttributeUiType.Document;
  }

  isDateRangeType(): boolean {
    return this.type === AttributeUiType.DateRange;
  }

  isComplexType(): boolean {
    return this.type === AttributeUiType.Complex;
  }

  static parse(obj: any): Attribute {
    let attribute = new Attribute(
      obj.id,
      obj.name,
      obj.description,
      obj.adminDocumentation,
      obj.value,
      obj.type,
      obj.entityType,
      obj.attributeDefinitionId,
      obj.required,
      obj.isReadOnly,
      obj.isHidden,
      obj.featured,
      obj.allowsUserAddedValues,
      obj.createdBy,
      obj.createdOn,
      obj.updatedBy,
      obj.updatedOn
    );
    attribute.attributeDefinitionValueListItem = obj.attributeDefinitionValueListItem
      ? AttributeDefinitionValueListItem.parse(obj.attributeDefinitionValueListItem)
      : null;
    attribute.valueList = obj.valueList
      ? _.map(obj.valueList, function (obj) {
          return AttributeDefinitionValueListItem.parse(obj);
        })
      : new Array<AttributeDefinitionValueListItem>();
    attribute.complexAttributeSubFieldValue = obj.complexAttributeSubFieldValue
      ? ComplexAttributeSubFieldValue.parse(obj.complexAttributeSubFieldValue)
      : null;
    attribute.subFieldList = obj.subFieldList
      ? obj.subFieldList.map((subField) => {
          return AttributeDefinitionSubFieldItem.parse(subField);
        })
      : null;

    return attribute;
  }

  static fromKeyValue(data: Array<RankedKeyValues>): Array<Attribute> {
    if (!data) {
      return new Array<Attribute>();
    }
    if (data.find((d) => d.type)) {
      return Attribute.fromKeyValue_NewType(data);
    }
    return _.flatMap(data, (k) => {
      if (_.isEmpty(k.values)) {
        return Attribute.parse({
          id: k.key,
          name: k.key,
          description: k.description,
          type: AttributeUiType.ShortAnswer,
          entityType: AttributeEntityType.Person,
          attributeDefinitionId: k.key,
          value: null,
        });
      }

      return _.map(k.values, (v) => {
        return Attribute.parse({
          id: k.key,
          name: k.key,
          description: k.description,
          type: AttributeUiType.DropDown,
          entityType: AttributeEntityType.Person,
          attributeDefinitionId: k.key,
          value: v.includes('AttributeDefinitionListItemValue')
            ? v.substring(v.lastIndexOf(':') + 1)
            : v,
          attributeDefinitionValueListItem: {
            itemValue: v,
            itemDescription: v,
          },
        });
      });
    });
  }

  static fromKeyValue_NewType(data: Array<RankedKeyValues>): Array<Attribute> {
    if (!data) {
      return new Array<Attribute>();
    }
    return _.flatMap(data, (k) => {
      if (_.isEmpty(k.values)) {
        return Attribute.parse({
          id: k.key,
          name: k.key,
          description: k.description,
          type: k.type,
          entityType: AttributeEntityType.Person,
          attributeDefinitionId: k.key,
          value: null,
        });
      }

      return _.flatMap(k.values, (v1, i) => {
        if (k.type === AttributeUiType.Complex) {
          let compexValues = JSON.parse(v1);
          return compexValues.flatMap((c) => {
            return c.Values.flatMap((v2) => {
              return Attribute.parse({
                id: k.key,
                name: k.key,
                description: k.description,
                type: AttributeUiType.Complex,
                entityType: AttributeEntityType.Person,
                attributeDefinitionId: k.key,
                value: null,
                complexAttributeSubFieldValue: ComplexAttributeSubFieldValue.parse({
                  subFieldDefinitionItemId: c.Key,
                  groupId: i,
                  value: v2.Value,
                  name: c.Key,
                  type: c.Type,
                  rank: c.Rank,
                  attributeDefinitionValueListItem: {
                    id: v2.AttributeDefinitionValueListItemId,
                    itemValue: v2.Value,
                    rank: v2.Rank,
                  },
                }),
              });
            });
          });
        } else {
          return Attribute.parse({
            id: k.key,
            name: k.key,
            description: k.description,
            type: k.type,
            entityType: AttributeEntityType.Person,
            attributeDefinitionId: k.key,
            value: JSON.parse(v1).Value,
            attributeDefinitionValueListItem: {
              id: JSON.parse(v1).AttributeDefinitionValueListItemId,
              itemValue: JSON.parse(v1).Value,
              rank: JSON.parse(v1).Rank,
            },
          });
        }
      });
    });
  }
}
